import { store, view } from '@aha-app/react-easy-state';
import Color from 'color';
import {
  observe,
  unobserve,
} from 'components/framework/lib/MicrotaskScheduler';
import $ from 'jquery';
import React, { useEffect, useState } from 'react';

const DEFAULT_THEME = 'light';

// Store the theme in a react easy state so React objects can have rerenders
// triggered when the theme changes.
window.themeStore = store({ theme: DEFAULT_THEME });

const isRunningInJest =
  typeof process !== 'undefined' && process.env.JEST_WORKER_ID !== undefined;

function darkModeEnabled() {
  return window.themeStore && window.themeStore.theme === 'dark';
}

function preferredSystemThemeChanged(event) {
  if (document?.body?.getAttribute('data-use-system-theme') !== null) {
    savePreferredSystemTheme();
  }
}

if (window.matchMedia) {
  let match = window.matchMedia('(prefers-color-scheme: dark)');
  // Safari v13 has matchMedia but no addEventListener
  if (match.addEventListener) {
    match.addEventListener('change', preferredSystemThemeChanged);
  }
}

// Initialize the theme store
['DOMContentLoaded', 'page:load'].forEach(event => {
  document.addEventListener(event, () => {
    if (!document.body.classList.contains('dark-mode-allowed')) {
      // Force theme to light to avoid leaking dark mode outside of develop
      setThemeStore(DEFAULT_THEME);
      return;
    }

    const theme = document.body.getAttribute('data-theme');
    setThemeStore(theme);

    // Update the preferred system theme if it has changed
    const savedSystemTheme = document.body.getAttribute(
      'data-use-system-theme'
    );
    if (savedSystemTheme !== null) {
      const savedSystemTheme = document.body.getAttribute('data-theme');
      const systemTheme = getSystemTheme();

      if (savedSystemTheme !== systemTheme) {
        savePreferredSystemTheme();
      }
    }
  });
});

function setThemeStore(theme) {
  window.themeStore.theme = theme;
}

// Always returns "light" or "dark" by converting "system" based on the users
// preferred system settings.
function translateTheme(theme) {
  return theme === 'system' ? getSystemTheme() : theme;
}

function getSystemTheme() {
  return window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

// Add the header to all AJAX requests
//
// This is required because you may have a tab open that is on a different theme
// then is stored server side in your preferences.
if (!isRunningInJest) {
  $(document).ajaxSend((event, xhr, settings) => {
    xhr.setRequestHeader(
      'X-Dark-Mode',
      document.body.getAttribute('data-theme') === 'dark'
    );
  });
}

function toggleTheme() {
  const currentTheme = document.body.getAttribute('data-theme');
  setTheme(currentTheme === 'dark' ? 'light' : 'dark');
}

// Change from dark to light mode or back. If you are on "system", then it
// sets you to the opposite of your system preferred scheme.
function setTheme(newTheme) {
  persistTheme(newTheme);

  if (newTheme === 'system') {
    document.body.setAttribute('data-use-system-theme', '');
    newTheme = translateTheme(newTheme);
  } else {
    document.body.removeAttribute('data-use-system-theme');
  }

  withCoolTransition(() => {
    document.body.setAttribute('data-theme', newTheme);
  });

  setThemeStore(newTheme);
}

// Save to the users preferences
function persistTheme(theme) {
  let body = `user[color_scheme]=${theme}&silent=true`;

  if (theme === 'system') {
    body += `&user[system_color_scheme]=${getSystemTheme()}`;
  }

  fetch('/settings/profile', {
    method: 'PATCH',
    headers: {
      'Content-type': 'application/x-www-form-urlencoded; charset=UTF-8',
      'X-CSRF-Token': window.csrfToken(),
    },
    body: body,
  });
}

function savePreferredSystemTheme() {
  persistTheme('system');
}

// Add a stylesheet to the body which makes switching to another mode fade
// between the two. Remove the stylesheet when done.
function withCoolTransition(wrappedFunction) {
  const style = document.createElement('style');
  style.type = 'text/css';
  style.innerHTML = '* { transition: background-color 0.3s, border 0.3s; }';
  document.body.append(style);

  wrappedFunction();

  setTimeout(() => {
    document.body.removeChild(style);
  }, 300);
}

const PALETTE_COLORS_LIGHT_THEME_TO_DARK = {
  '#ee9999': '#a83b3b',
  '#e24d4d': '#872929',
  '#d50000': '#731717',
  '#aa0000': '#590909',
  '#800000': '#400000',
  '#fdd4a3': '#99672b',
  '#fbb35f': '#875c29',
  '#f9931a': '#6e4516',
  '#c77615': '#543208',
  '#955810': '#3c2000',
  '#ffee99': '#a39241',
  '#ffe24d': '#857428',
  '#ffd500': '#6b5c15',
  '#ccaa00': '#524608',
  '#998000': '#382f00',
  '#dfefc5': '#75943b',
  '#c8e29a': '#5c7a25',
  '#b0d66e': '#476314',
  '#8dab58': '#324a07',
  '#6a8042': '#1e3000',
  '#c1e39d': '#5f8f2b',
  '#93cd54': '#4c781d',
  '#64b80a': '#3b6111',
  '#509308': '#284707',
  '#3c6e06': '#1a3300',
  '#bddcdd': '#389394',
  '#8cc1c3': '#22797a',
  '#5ba7a9': '#146566',
  '#498687': '#084b4d',
  '#376465': '#003233',
  '#99c7ec': '#226eab',
  '#4d9ddd': '#1a5c91',
  '#0173cf': '#0173cf',
  '#015ca6': '#015ca6',
  '#01457c': '#01457c',
  '#c5dfe9': '#408199',
  '#9ac8d9': '#29708a',
  '#6fb0c8': '#165870',
  '#598da0': '#094257',
  '#436a78': '#002d3e',
  '#c2b7c8': '#9775a9',
  '#95829e': '#815d92',
  '#674c75': '#5d3a6f',
  '#523d5e': '#432653',
  '#3e2e46': '#2f143d',
  '#ddbec7': '#a3526b',
  '#c38d9e': '#8a3751',
  '#a95c74': '#701c36',
  '#874a5d': '#570921',
  '#653746': '#3d0013',
  '#f7c6c3': '#a34841',
  '#f09c95': '#8a2f29',
  '#ea7168': '#701c16',
  '#bb5a53': '#570e09',
  '#8c443e': '#3d0400',
  '#f1e2cf': '#99764d',
  '#e7cdab': '#805c2e',
  '#dcb787': '#664214',
  '#b0926c': '#4d2e08',
  '#846e51': '#341d00',
  '#000000': '#e8eef3',
  '#4c4c4c': '#cbd5de',
  '#666666': '#b3bcc4',
  '#7f7f7f': '#9ca4ab',
  '#a5a5a5': '#78828a',
  '#cccccc': '#5a6673',
  '#d9d9d9': '#48525c',
  '#e5e5e5': '#3c454d',
  '#f2f2f2': '#2d363d',
  '#ffffff': '#21282e',
};

function mapColorFromColorPickerToDarkMode(color) {
  // Memoize this and toggle the memo when the user changes the theme
  const darkMode =
    document.querySelector('body').getAttribute('data-theme') === 'dark';

  if (!darkMode) {
    return color;
  }

  return PALETTE_COLORS_LIGHT_THEME_TO_DARK[color] || color;
}

/**
 * Mix a color for the current theme. The mixes are #21282e for dark and white for light
 *
 * @param {Parameters<typeof Color>[0]} color
 * @param {number=} weight
 */
function mixColorForTheme(color, weight = 0.6) {
  const mixWith = new Color(darkModeEnabled() ? '#21282e' : 'white');
  return Color(color).mix(mixWith, weight);
}

// Higher order component to inject the theme into another React component
// NOTE: If your WrappedComponent also depends on react-easy-state, you should pass in view(WrappedComponent)
/**
 * @template {{}} Props
 * @param {React.ComponentType<Props>} WrappedComponent
 * @returns {React.ComponentType<Props>}
 */
function withTheme(WrappedComponent) {
  const displayName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';
  const WithTheme = Object.assign(
    props => {
      const darkMode = darkModeEnabled();
      return <WrappedComponent darkMode={darkMode} {...props} />;
    },
    { displayName: `WithTheme(${displayName})` }
  );
  return view(WithTheme);
}

/**
 * Hook to get the theme from any react component
 */
function useTheme() {
  const [theme, setTheme] = useState(window.themeStore.theme);

  useEffect(() => {
    const observer = observe(() => {
      setTheme(window.themeStore.theme);
    });
    return () => unobserve(observer);
  }, []);

  return theme;
}

export {
  setTheme,
  toggleTheme,
  translateTheme,
  mapColorFromColorPickerToDarkMode,
  mixColorForTheme,
  withTheme,
  useTheme,
};
